import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  Button,
  Alert,
  Chip
} from '@mui/material';
import { ThemeProvider, createTheme, alpha } from '@mui/material/styles';
import {
  Save,
  Security,
  Dashboard,
  PointOfSale,
  ShoppingCart,
  Assignment,
  CorporateFare,
  People,
  Assessment
} from '@mui/icons-material';
import { roleAPI, permissionAPI, moduleAPI } from '../services/api';

const getTheme = () =>
  createTheme({
    palette: {
      mode: 'light',
      primary: { main: '#005b82', light: '#2586bc', dark: '#003d5c' },
      background: { default: '#f8fafc', paper: '#ffffff' },
    },
    shape: { borderRadius: 12 },
  });

const MODULE_ICONS = {
  dashboard: Dashboard,
  sales_tracker: PointOfSale,
  purchase_tracker: ShoppingCart,
  order_tracker: Assignment,
  vendor: CorporateFare,
  currency: CorporateFare,
  user_management: People,
  role_management: Security,
  reports: Assessment,
};

const getModuleIcon = (code) => MODULE_ICONS[code] || Security;

const PermissionManagement = () => {
  const [roles, setRoles] = useState([]);
  const [modules, setModules] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [selectedRole, setSelectedRole] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [rolesRes, modulesRes, permissionsRes] = await Promise.all([
        roleAPI.getRoles(),
        moduleAPI.getModules(),
        permissionAPI.getPermissions()
      ]);
      setRoles(rolesRes.data);
      setModules(modulesRes.data);
      setPermissions(permissionsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      showAlert('Error loading data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showAlert = (message, severity = 'success') => {
    setAlert({ open: true, message, severity });
    setTimeout(() => setAlert({ open: false, message: '', severity: 'success' }), 5000);
  };

  const handlePermissionChange = async (moduleId, permissionType, checked) => {
    if (!selectedRole) {
      showAlert('Please select a role first', 'warning');
      return;
    }

    const existingPermission = permissions.find(
      p => p.role === selectedRole && p.module === moduleId
    );

    const permissionData = {
      role: selectedRole,
      module: moduleId,
      can_create: permissionType === 'can_create' ? checked : (existingPermission?.can_create || false),
      can_read: permissionType === 'can_read' ? checked : (existingPermission?.can_read || false),
      can_update: permissionType === 'can_update' ? checked : (existingPermission?.can_update || false),
      can_delete: permissionType === 'can_delete' ? checked : (existingPermission?.can_delete || false),
    };

    try {
      setSaving(true);
      if (existingPermission) {
        await permissionAPI.updatePermission(existingPermission.id, permissionData);
      } else {
        await permissionAPI.createPermission(permissionData);
      }
      showAlert('Permission updated successfully');
      fetchData(); // Refresh permissions
    } catch (error) {
      console.error('Error updating permission:', error);
      showAlert('Error updating permission', 'error');
    } finally {
      setSaving(false);
    }
  };

  const getPermissionForModule = (moduleId) => {
    return permissions.find(p => p.role === selectedRole && p.module === moduleId);
  };

  const selectedRoleData = roles.find(r => r.id === selectedRole);

  const theme = getTheme();

  if (loading) {
    return (
      <Container maxWidth="xl">
        <Typography>Loading permissions...</Typography>
      </Container>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg,#f8fafc 0%,#e2e8f0 100%)',
          pb: 4,
        }}
      >
        <Container maxWidth="xl" sx={{ py: 3 }}>
          {alert.open && (
            <Alert severity={alert.severity} sx={{ mb: 2 }}>
              {alert.message}
            </Alert>
          )}

          <Box sx={{ mb: 4 }}>
            <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
              Permission Management
            </Typography>
            <Typography variant="body1" color="textSecondary">
              Manage module access permissions for different roles
            </Typography>
          </Box>

          {/* Role Selection */}
          <Card sx={{ mb: 4 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
                <FormControl sx={{ minWidth: 260 }}>
                  <InputLabel>Select Role</InputLabel>
                  <Select
                    value={selectedRole}
                    onChange={(e) => setSelectedRole(e.target.value)}
                    label="Select Role"
                  >
                    {roles.map((role) => (
                      <MenuItem key={role.id} value={role.id}>
                        {role.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {selectedRoleData && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Chip
                      label={selectedRoleData.name}
                      sx={{
                        background: alpha('#005b82', 0.1),
                        color: '#005b82',
                        fontWeight: 600,
                      }}
                    />
                    <Typography variant="body2" color="textSecondary">
                      {selectedRoleData.description || 'No description available'}
                    </Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>

          {selectedRole ? (
            <>
              {/* Permissions Table */}
              <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 3, overflow: 'hidden' }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: alpha('#005b82', 0.06) }}>
                      <TableCell><strong>Module</strong></TableCell>
                      <TableCell align="center"><strong>Read</strong></TableCell>
                      <TableCell align="center"><strong>Create</strong></TableCell>
                      <TableCell align="center"><strong>Update</strong></TableCell>
                      <TableCell align="center"><strong>Delete</strong></TableCell>
                      <TableCell><strong>Access Level</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {modules.map((module) => {
                      const permission = getPermissionForModule(module.id);
                      const hasAnyPermission = permission && (
                        permission.can_read || permission.can_create ||
                        permission.can_update || permission.can_delete
                      );
                      const ModuleIcon = getModuleIcon(module.code);

                      return (
                        <TableRow key={module.id} sx={{ '&:hover': { backgroundColor: 'grey.50' } }}>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  p: 1,
                                  borderRadius: 2,
                                  background: alpha('#005b82', 0.1),
                                  color: '#005b82',
                                }}
                              >
                                <ModuleIcon fontSize="small" />
                              </Box>
                              <Box>
                                <Typography variant="subtitle1" fontWeight="bold">
                                  {module.name}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                  {module.code}
                                </Typography>
                                {module.description && (
                                  <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                                    {module.description}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          </TableCell>

                          <TableCell align="center">
                            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                              <Switch
                                checked={permission?.can_read || false}
                                onChange={(e) => handlePermissionChange(module.id, 'can_read', e.target.checked)}
                                color="primary"
                              />
                            </Box>
                          </TableCell>

                          <TableCell align="center">
                            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                              <Switch
                                checked={permission?.can_create || false}
                                onChange={(e) => handlePermissionChange(module.id, 'can_create', e.target.checked)}
                                color="primary"
                              />
                            </Box>
                          </TableCell>

                          <TableCell align="center">
                            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                              <Switch
                                checked={permission?.can_update || false}
                                onChange={(e) => handlePermissionChange(module.id, 'can_update', e.target.checked)}
                                color="primary"
                              />
                            </Box>
                          </TableCell>

                          <TableCell align="center">
                            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                              <Switch
                                checked={permission?.can_delete || false}
                                onChange={(e) => handlePermissionChange(module.id, 'can_delete', e.target.checked)}
                                color="primary"
                              />
                            </Box>
                          </TableCell>

                          <TableCell>
                            {hasAnyPermission ? (
                              <Chip
                                label="Granted"
                                color="success"
                                size="small"
                                variant="outlined"
                              />
                            ) : (
                              <Chip
                                label="No Access"
                                color="default"
                                size="small"
                                variant="outlined"
                              />
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Quick Actions */}
              <Box sx={{ mt: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  startIcon={<Save />}
                  onClick={() => {
                    // Grant all permissions
                    modules.forEach(module => {
                      handlePermissionChange(module.id, 'can_read', true);
                      handlePermissionChange(module.id, 'can_create', true);
                      handlePermissionChange(module.id, 'can_update', true);
                      handlePermissionChange(module.id, 'can_delete', true);
                    });
                  }}
                  disabled={saving}
                  sx={{
                    background: 'linear-gradient(135deg,#005b82 0%,#2586bc 100%)',
                  }}
                >
                  Grant All Permissions
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => {
                    // Revoke all permissions
                    modules.forEach(module => {
                      handlePermissionChange(module.id, 'can_read', false);
                      handlePermissionChange(module.id, 'can_create', false);
                      handlePermissionChange(module.id, 'can_update', false);
                      handlePermissionChange(module.id, 'can_delete', false);
                    });
                  }}
                  disabled={saving}
                >
                  Revoke All Permissions
                </Button>
              </Box>
            </>
          ) : (
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 6 }}>
                <Security sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="textSecondary" gutterBottom>
                  Select a Role to Manage Permissions
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Choose a role from the dropdown above to view and modify its permissions
                </Typography>
              </CardContent>
            </Card>
          )}
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default PermissionManagement;
